import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { BRAND } from "@/data/site";

const TEL = BRAND.phone.replace(/\s/g, "");
const WA = BRAND.whatsapp.replace(/[^\d]/g, "");

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/databases", label: "Databases" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-slate-100"
            : "bg-white/40 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-brand"
            className="flex items-center gap-2 group"
            aria-label="Datorra India — Home"
          >
            <span className="text-blue-600 text-2xl leading-none transition-transform duration-500 group-hover:rotate-90">◐</span>
            <span className="font-display font-medium tracking-tight text-lg text-slate-900">
              Datorra <span className="text-slate-400 font-light">/ India</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute left-3 right-3 -bottom-0.5 h-px bg-slate-900"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={`tel:${TEL}`}
              data-testid="nav-cta-call"
              aria-label="Call Datorra India now"
              className="group inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-900 pl-4 pr-4 py-2.5 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 transition-colors"
            >
              <Phone className="w-3.5 h-3.5 text-blue-600" strokeWidth={2} />
              Call Now
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-quote"
              className="group inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white pl-5 pr-4 py-2.5 text-sm font-medium transition-all duration-300 hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95"
            >
              Get a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 -mr-2 text-slate-900"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 top-16 z-40 md:hidden bg-white"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 py-10 flex flex-col gap-2 border-t border-slate-100">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="block py-4 text-3xl font-display font-light tracking-tight text-slate-900 border-b border-slate-100"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-4 text-base font-medium"
              >
                Get a Quote <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
              <div className="mt-10 text-sm text-slate-500 space-y-1">
                <div>{BRAND.email}</div>
                <div>{BRAND.phone}</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
